import { useState, useEffect, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  collectDeviceFingerprint,
  DeviceFingerprint,
  loadStoredDeviceFingerprint,
  storeDeviceFingerprint,
  clearStoredDeviceFingerprint,
  loadStoredDeviceKey,
  storeDeviceKey,
  clearStoredDeviceKey,
} from '../utils/deviceFingerprinting';

const AUTHENTICATE_DEVICE = gql`
  mutation AuthenticateDevice($characteristics: DeviceFingerprintInput!, $deviceKey: String) {
    authenticateDevice(characteristics: $characteristics, deviceKey: $deviceKey) {
      isValid
      device {
        id
        deviceName
        status
        lastSeen
        location
        approver {
          firstName
          lastName
        }
      }
      reason
      token
    }
  }
`;

const CLAIM_DEVICE_KEY = gql`
  mutation ClaimDeviceKey($characteristics: DeviceFingerprintInput!) {
    claimDeviceKey(characteristics: $characteristics) {
      success
      deviceKey
      reason
    }
  }
`;

export interface DeviceVerificationResult {
  isValid: boolean;
  device?: {
    id: string;
    deviceName: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'BLOCKED';
    lastSeen?: string;
    location?: string;
    approver?: {
      firstName: string;
      lastName: string;
    };
  };
  reason?: string;
  token?: string;
}

export interface UseDeviceVerificationResult {
  isVerified: boolean;
  isLoading: boolean;
  error: string | null;
  verificationResult: DeviceVerificationResult | null;
  deviceFingerprint: DeviceFingerprint | null;
  verifyDevice: () => Promise<void>;
  clearVerification: () => void;
  retryVerification: () => Promise<void>;
}

export const useDeviceVerification = (): UseDeviceVerificationResult => {
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verificationResult, setVerificationResult] = useState<DeviceVerificationResult | null>(null);
  const [deviceFingerprint, setDeviceFingerprint] = useState<DeviceFingerprint | null>(null);

  const [authenticateDeviceMutation] = useMutation(AUTHENTICATE_DEVICE);
  const [claimDeviceKeyMutation] = useMutation(CLAIM_DEVICE_KEY);

  const convertFingerprintToInput = useCallback((fingerprint: DeviceFingerprint) => {
    return {
      userAgent: fingerprint.userAgent || 'Unknown',
      acceptLanguage: fingerprint.acceptLanguage || 'en',
      acceptEncoding: fingerprint.acceptEncoding || 'gzip, deflate',
      timezone: fingerprint.timezone || 'UTC',
      platform: fingerprint.platform || 'Unknown',
      screen: {
        width: fingerprint.screen?.width || 1920,
        height: fingerprint.screen?.height || 1080,
        colorDepth: fingerprint.screen?.colorDepth || 24,
        pixelRatio: fingerprint.screen?.pixelRatio || 1.0,
      },
      browserFeatures: {
        canvas: fingerprint.browserFeatures?.canvas || false,
        cookieEnabled: fingerprint.browserFeatures?.cookieEnabled || true,
        doNotTrack: fingerprint.browserFeatures?.doNotTrack || false,
        localStorage: fingerprint.browserFeatures?.localStorage || true,
        sessionStorage: fingerprint.browserFeatures?.sessionStorage || true,
        touchSupport: fingerprint.browserFeatures?.touchSupport || false,
        webGL: fingerprint.browserFeatures?.webGL || false,
      },
      canvas: fingerprint.canvas ? fingerprint.canvas.substring(0, 100) : 'canvas-not-available',
      webgl: fingerprint.webgl || 'webgl-not-available',
      audio: fingerprint.audio || null,
      fonts: fingerprint.fonts || ['Arial'],
      plugins: fingerprint.plugins || [],
      hardwareConcurrency: fingerprint.hardwareConcurrency || 1,
      deviceMemory: fingerprint.deviceMemory || 1,
      connection: fingerprint.connection || {
        effectiveType: '4g',
        downlink: 10.0,
        rtt: 100
      },
    };
  }, []);

  const performVerification = useCallback(async (fingerprint: DeviceFingerprint) => {
    console.log('[DeviceVerification] performVerification called');
    try {
      setIsLoading(true);
      setError(null);

      console.log('[DeviceVerification] Calling authenticateDevice mutation...');

      // Add timeout to prevent indefinite loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('TIMEOUT')), 15000); // 15 second timeout
      });

      const characteristicsInput = convertFingerprintToInput(fingerprint);
      const storedKey = loadStoredDeviceKey();

      const mutationPromise = authenticateDeviceMutation({
        variables: {
          characteristics: characteristicsInput,
          deviceKey: storedKey,
        },
        context: {
          fetchOptions: {
            timeout: 15000, // 15 second timeout
          },
        },
      });

      const result = await Promise.race([mutationPromise, timeoutPromise]) as any;

      console.log('[DeviceVerification] Mutation completed, processing result...');
      const verificationData = result?.data?.authenticateDevice;
      
      console.log('[DeviceVerification] Full verification result:', verificationData);
      
      if (verificationData) {
        setVerificationResult(verificationData);
        setIsVerified(verificationData.isValid);
        
        console.log('[DeviceVerification] Device verification result:', {
          isValid: verificationData.isValid,
          deviceStatus: verificationData.device?.status,
          reason: verificationData.reason
        });
        
        if (verificationData.isValid && verificationData.token) {
          // Store JWT token in sessionStorage (not localStorage for better security)
          sessionStorage.setItem('workflow_device_jwt', verificationData.token);
          console.log('[DeviceVerification] Device authenticated successfully, token stored');

          // First-time migration: ask backend for a stable deviceKey, store in
          // localStorage. After this, future verifications use the key and
          // tolerate userAgent / screen / timezone changes.
          if (!storedKey) {
            try {
              const claimResult = await claimDeviceKeyMutation({
                variables: { characteristics: characteristicsInput },
              });
              const claimedKey = claimResult?.data?.claimDeviceKey?.deviceKey;
              if (claimedKey) {
                storeDeviceKey(claimedKey);
                console.log('[DeviceVerification] Stable deviceKey claimed and stored');
              }
            } catch (claimErr) {
              console.warn('[DeviceVerification] Failed to claim deviceKey (non-fatal):', claimErr);
            }
          }
        } else if (!verificationData.isValid) {
          console.log('[DeviceVerification] Device verification failed:', verificationData.reason);
          setError(verificationData.reason || 'Weryfikacja urządzenia nie powiodła się');
        }
      } else {
        console.log('[DeviceVerification] No verification data received');
        setError('Brak odpowiedzi z serwera');
      }
    } catch (err: any) {
      console.error('Device verification failed:', err);

      // Handle timeout specifically
      if (err.message === 'TIMEOUT') {
        setError('Przekroczono limit czasu weryfikacji urządzenia. Sprawdź połączenie z siecią i spróbuj ponownie.');
      } else if (err.networkError) {
        setError('Błąd połączenia sieciowego. Sprawdź połączenie z internetem i spróbuj ponownie.');
      } else {
        setError('Błąd podczas weryfikacji urządzenia');
      }

      setIsVerified(false);
    } finally {
      setIsLoading(false);
    }
  }, [authenticateDeviceMutation, claimDeviceKeyMutation, convertFingerprintToInput]);

  const verifyDevice = useCallback(async () => {
    console.log('[DeviceVerification] Starting device verification...');
    try {
      setIsLoading(true);
      setError(null);

      // Try to load stored fingerprint first
      let fingerprint = loadStoredDeviceFingerprint();
      console.log('[DeviceVerification] Stored fingerprint:', fingerprint ? 'Found' : 'Not found');
      
      if (!fingerprint) {
        console.log('[DeviceVerification] Collecting fresh fingerprint...');
        // Collect fresh fingerprint
        fingerprint = await collectDeviceFingerprint();
        storeDeviceFingerprint(fingerprint);
        console.log('[DeviceVerification] Fresh fingerprint collected and stored');
      }

      setDeviceFingerprint(fingerprint);
      console.log('[DeviceVerification] Starting API verification...');
      await performVerification(fingerprint);
    } catch (err) {
      console.error('Device fingerprint collection failed:', err);
      setError('Nie udało się zebrać informacji o urządzeniu');
      setIsLoading(false);
    }
  }, [performVerification]);

  const retryVerification = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Always collect fresh fingerprint on retry
      const fingerprint = await collectDeviceFingerprint();
      storeDeviceFingerprint(fingerprint);
      setDeviceFingerprint(fingerprint);
      
      await performVerification(fingerprint);
    } catch (err) {
      console.error('Device fingerprint collection failed:', err);
      setError('Nie udało się zebrać informacji o urządzeniu');
      setIsLoading(false);
    }
  }, [performVerification]);

  const clearVerification = useCallback(() => {
    setIsVerified(false);
    setError(null);
    setVerificationResult(null);
    setDeviceFingerprint(null);
    clearStoredDeviceFingerprint();
    clearStoredDeviceKey();
  }, []);

  // Auto-verify on mount - always use API verification
  useEffect(() => {
    verifyDevice();
  }, [verifyDevice]);

  // Set up periodic verification (every 5 minutes)
  useEffect(() => {
    if (isVerified) {
      const interval = setInterval(() => {
        verifyDevice();
      }, 5 * 60 * 1000); // 5 minutes

      return () => clearInterval(interval);
    }
  }, [isVerified, verifyDevice]);

  // Listen for storage changes (if user clears cache in another tab)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'workflow-device-fingerprint' && e.newValue === null) {
        // Fingerprint was cleared, re-verify
        verifyDevice();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [verifyDevice]);

  return {
    isVerified,
    isLoading,
    error,
    verificationResult,
    deviceFingerprint,
    verifyDevice,
    clearVerification,
    retryVerification,
  };
};